import { supabase } from '../supabaseClient';
import { logAudit } from './auditLog';
import { createBatchNotifications, sendEmailNotification, buildEmailHtml, sendTeamsNotification } from './notifications';
import { isNotificationEnabledBatch } from './notificationPreferences';
import { updateTicketActivity } from './ticketActivity';
import { syncTicketReferences } from './ticketReferences';
import { getCachedUserAndProfile } from '../utils/userCache';

/** 將 @[Name](uuid) 轉為 @Name，用於通知純文字摘要 */
const stripMentionMarkup = (text) =>
  text.replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1');

/** 查詢多位用戶的 email（用於寄送 mention email 通知） */
const getEmailsByUserIds = async (userIds) => {
  if (!userIds.length) return {};
  const { data } = await supabase
    .from('profiles')
    .select('id, email')
    .in('id', userIds);
  if (!data) return {};
  return Object.fromEntries(data.map((p) => [p.id, p.email]));
};

/**
 * 取得 Ticket 的所有留言
 */
export const getCommentsByTicket = async (ticketId) => {
  try {
    const { data, error } = await supabase
      .from('ticket_comments')
      .select(`
        *,
        author:author_id (id, email)
      `)
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching comments:', error);
    return { data: null, error };
  }
};

/**
 * 建立留言
 */
export const createComment = async (data) => {
  try {
    // Parse @mentions from content
    const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
    const mentions = [];
    let match;
    while ((match = mentionRegex.exec(data.content)) !== null) {
      mentions.push(match[2]); // UUID
    }

    const insertData = {
      ticket_id: data.ticket_id,
      author_id: data.author_id,
      content: data.content,
      mentions,
    };

    const { data: comment, error } = await supabase
      .from('ticket_comments')
      .insert(insertData)
      .select(`
        *,
        author:author_id (id, email)
      `)
      .single();

    if (error) throw error;

    // Audit log
    const { data: ticketMeta } = await supabase
      .from('tickets')
      .select('company_id, department_id')
      .eq('id', data.ticket_id)
      .single();
    logAudit('create', 'ticket_comment', comment.id, null, {
      after: { content: data.content, ticket_id: data.ticket_id, author_id: data.author_id },
    }, { ticket_id: data.ticket_id },
      ticketMeta?.department_id,
      ticketMeta?.company_id
    );

    await updateTicketActivity(data.ticket_id, data.author_id);

    // 同步 ticket references（fire-and-forget）
    syncTicketReferences(data.ticket_id, ticketMeta?.company_id, data.author_id);

    // 對被 tag 的人發送通知（排除自己）
    const mentionTargets = mentions.filter((userId) => userId !== data.author_id);
    if (mentionTargets.length > 0) {
      // 查 ticket 的 ticket_no / title / project name
      const { data: ticketInfo } = await supabase
        .from('tickets')
        .select('ticket_no, title, projects:project_id (name)')
        .eq('id', data.ticket_id)
        .single();

      const projectName = ticketInfo?.projects?.name || '';
      const ticketNo = ticketInfo?.ticket_no;
      const ticketTitle = ticketInfo?.title || '';
      const ticketLabel = ticketNo
        ? `${projectName} #${ticketNo} ${ticketTitle}`.trim()
        : ticketTitle;
      const notifTitle = ticketNo
        ? `在 ${projectName} #${ticketNo} 的留言中提及了你`
        : '你在留言中被提及';

      const snippet = stripMentionMarkup(data.content).slice(0, 100) + (data.content.length > 100 ? '...' : '');

      // 批次查詢所有被 mention 者的通知偏好
      const prefs = await isNotificationEnabledBatch(mentionTargets, 'mentioned');

      // 站內通知（只發給有開啟 in_app 的人）
      const inAppTargets = mentionTargets.filter((uid) => prefs[uid]?.in_app !== false);
      if (inAppTargets.length > 0) {
        const notifications = inAppTargets.map((userId) => ({
          user_id: userId,
          type: 'mention',
          title: notifTitle,
          message: snippet,
          resource_type: 'ticket',
          resource_id: String(data.ticket_id),
          actor_id: data.author_id,
        }));
        await createBatchNotifications(notifications);
      }

      // Email 通知（只發給有開啟 email 的人，fire-and-forget）
      const emailTargets = mentionTargets.filter((uid) => prefs[uid]?.email !== false);
      if (emailTargets.length > 0) {
        const emailSnippet = stripMentionMarkup(data.content).slice(0, 200);
        getEmailsByUserIds(emailTargets).then((emailMap) => {
          Object.entries(emailMap).forEach(([, email]) => {
            if (email) {
              sendEmailNotification({
                to: email,
                subject: `[Sonice] 在 ${ticketLabel} 的留言中提及了你`,
                html: buildEmailHtml({
                  title: notifTitle,
                  message: emailSnippet,
                  ticketId: data.ticket_id,
                  ticketLabel,
                }),
                type: 'mention',
              });
            }
          });
        });
      }

      // Teams Webhook 通知（fire-and-forget，subscribed_events 由 webhook 自己控制）
      // targetEmail = 被 @mention 者的 email
      getEmailsByUserIds(mentionTargets).then((emailMap) => {
        mentionTargets.forEach((userId) => {
          sendTeamsNotification({
            user_id: userId,
            event_type: 'mentioned',
            title: notifTitle,
            message: snippet,
            ticketId: data.ticket_id,
            targetEmail: emailMap[userId] || null,
          });
        });
      });
    }

    return { data: comment, error: null };
  } catch (error) {
    console.error('Error creating comment:', error);
    return { data: null, error };
  }
};

/**
 * 更新留言
 */
export const updateComment = async (commentId, content) => {
  try {
    // 取得舊的 mentions 用來比對
    const { data: oldComment } = await supabase
      .from('ticket_comments')
      .select('mentions, author_id, ticket_id, content, tickets:ticket_id(company_id, department_id)')
      .eq('id', commentId)
      .single();

    const oldMentions = new Set(oldComment?.mentions || []);

    // Re-parse mentions
    const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
    const mentions = [];
    let match;
    while ((match = mentionRegex.exec(content)) !== null) {
      mentions.push(match[2]);
    }

    const { data, error } = await supabase
      .from('ticket_comments')
      .update({
        content,
        mentions,
        updated_at: new Date().toISOString(),
      })
      .eq('id', commentId)
      .select(`
        *,
        author:author_id (id, email)
      `)
      .single();

    if (error) throw error;

    // Audit log
    logAudit('update', 'ticket_comment', commentId, null, {
      before: { content: oldComment?.content },
      after: { content },
    }, { ticket_id: oldComment?.ticket_id },
      oldComment?.tickets?.department_id,
      oldComment?.tickets?.company_id
    );

    await updateTicketActivity(oldComment?.ticket_id, oldComment?.author_id);

    // 同步 ticket references（fire-and-forget）
    syncTicketReferences(oldComment?.ticket_id, oldComment?.tickets?.company_id, oldComment?.author_id);

    // 對新增的 mention 發送通知（排除自己和已通知過的人）
    const newMentions = mentions.filter((uid) => !oldMentions.has(uid) && uid !== oldComment?.author_id);
    if (newMentions.length > 0) {
      const ticketId = oldComment?.ticket_id;

      // 查 ticket 的 ticket_no / title / project name
      const { data: ticketInfo } = await supabase
        .from('tickets')
        .select('ticket_no, title, projects:project_id (name)')
        .eq('id', ticketId)
        .single();

      const projectName = ticketInfo?.projects?.name || '';
      const ticketNo = ticketInfo?.ticket_no;
      const ticketTitle = ticketInfo?.title || '';
      const ticketLabel = ticketNo
        ? `${projectName} #${ticketNo} ${ticketTitle}`.trim()
        : ticketTitle;
      const notifTitle = ticketNo
        ? `在 ${projectName} #${ticketNo} 的留言中提及了你`
        : '你在留言中被提及';

      const snippet = stripMentionMarkup(content).slice(0, 100) + (content.length > 100 ? '...' : '');

      // 批次查詢所有被 mention 者的通知偏好
      const prefs = await isNotificationEnabledBatch(newMentions, 'mentioned');

      // 站內通知（只發給有開啟 in_app 的人）
      const inAppTargets = newMentions.filter((uid) => prefs[uid]?.in_app !== false);
      if (inAppTargets.length > 0) {
        const notifications = inAppTargets.map((userId) => ({
          user_id: userId,
          type: 'mention',
          title: notifTitle,
          message: snippet,
          resource_type: 'ticket',
          resource_id: String(ticketId),
          actor_id: oldComment?.author_id,
        }));
        await createBatchNotifications(notifications);
      }

      // Email 通知（只發給有開啟 email 的人，fire-and-forget）
      const emailTargets = newMentions.filter((uid) => prefs[uid]?.email !== false);
      if (emailTargets.length > 0) {
        const emailSnippet = stripMentionMarkup(content).slice(0, 200);
        getEmailsByUserIds(emailTargets).then((emailMap) => {
          Object.entries(emailMap).forEach(([, email]) => {
            if (email) {
              sendEmailNotification({
                to: email,
                subject: `[Sonice] 在 ${ticketLabel} 的留言中提及了你`,
                html: buildEmailHtml({
                  title: notifTitle,
                  message: emailSnippet,
                  ticketId,
                  ticketLabel,
                }),
                type: 'mention',
              });
            }
          });
        });
      }

      // Teams Webhook 通知（fire-and-forget）
      // targetEmail = 被 @mention 者的 email
      getEmailsByUserIds(newMentions).then((emailMap) => {
        newMentions.forEach((userId) => {
          sendTeamsNotification({
            user_id: userId,
            event_type: 'mentioned',
            title: notifTitle,
            message: snippet,
            ticketId,
            targetEmail: emailMap[userId] || null,
          });
        });
      });
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error updating comment:', error);
    return { data: null, error };
  }
};

/**
 * 刪除留言
 */
export const deleteComment = async (commentId) => {
  try {
    // 先取得留言資訊用於日誌
    const { data: commentData } = await supabase
      .from('ticket_comments')
      .select('id, ticket_id, author_id, content, tickets:ticket_id(company_id, department_id)')
      .eq('id', commentId)
      .single();

    const { error } = await supabase
      .from('ticket_comments')
      .delete()
      .eq('id', commentId);

    if (error) throw error;

    await logAudit('delete', 'ticket_comment', commentId, null, {
      before: { content: commentData?.content, ticket_id: commentData?.ticket_id, author_id: commentData?.author_id },
    }, { ticket_id: commentData?.ticket_id },
      commentData?.tickets?.department_id,
      commentData?.tickets?.company_id
    );

    const { profile } = await getCachedUserAndProfile(supabase);
    await updateTicketActivity(commentData?.ticket_id, profile?.id);

    // 同步 ticket references（fire-and-forget）
    syncTicketReferences(commentData?.ticket_id, commentData?.tickets?.company_id, profile?.id);

    return { error: null };
  } catch (error) {
    console.error('Error deleting comment:', error);
    return { error };
  }
};
